import { CryptoStore } from "./crypto.store";
import { AsyncStore } from "@me8eon/async_storage";
import { Codec } from "@me8eon/codec";
import { ErrorsOr, flatMapErrorsOrK } from "@me8eon/errors";
import { aesDecodeFn, AesDecodeFn } from "./aesDecodeFn";
import { aesEncodeFn, AesEncodeFn } from "./aesEncodeFn";

export type EncryptedStore<Id, T> = {
    cryptoStore: CryptoStore<Id>
    rawStore: AsyncStore<Id, string>
    codec: Codec<T>
    defaultValue: () => ErrorsOr<T>
    _decode?: AesDecodeFn
    _encode?: AesEncodeFn
}

export function loadEncrypted<Id, T>({
                                         cryptoStore,
                                         rawStore,
                                         codec,
                                         defaultValue,
                                         _decode = aesDecodeFn
                                     }: EncryptedStore<Id, T>) {
    return async (id: Id, undefinedIfNotIn?: boolean): Promise<ErrorsOr<T>> => {
        return flatMapErrorsOrK(await cryptoStore.metaData(id), async meta =>
            flatMapErrorsOrK(await cryptoStore.secret(id), async secret =>
                flatMapErrorsOrK(await rawStore.get(id, true), async raw => {
                    if (raw === undefined) {
                        return undefinedIfNotIn ? { value: undefined as T } : defaultValue();
                    }

                    const decoded = await _decode(secret, meta.iv)(raw);
                    return codec.decode(decoded);
                })
            )
        );
    };
}

export function storeEncrypted<Id, T>({
                                          cryptoStore,
                                          rawStore,
                                          codec,
                                          _encode = aesEncodeFn
                                      }: EncryptedStore<Id, T>) {
    return async (id: Id, t: T): Promise<ErrorsOr<void>> => {
        return flatMapErrorsOrK(await cryptoStore.metaData(id), async meta =>
            flatMapErrorsOrK(await cryptoStore.secret(id), async secret =>
                flatMapErrorsOrK(codec.encode(t), async encodedValue => {
                    const encryptedValue = await _encode(secret, meta.iv)(encodedValue);
                    return rawStore.store(id, encryptedValue);
                })
            )
        );
    };
}

export function azureEncryptedBlobStore<Id, T>(config: EncryptedStore<Id, T>): AsyncStore<Id, T> {
    return {
        get: loadEncrypted(config),
        store: storeEncrypted(config),
        delete: config.rawStore.delete,
    };
}